import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';

import {
  createEvalCase,
  createGithubIssue,
  createKnowledgeArticle,
  runTriage,
  updateFeedbackStatus,
} from '../../../services/api/adminFeedback';
import { type FeedbackStatus } from '../../../types/feedback';

// Feedback-API:t svarar { error, details? } och aldrig { message }, så getApiErrorMessage
// skulle bara ge "Request failed with status code 502" här. details kan vara ett
// zod-flatten-objekt — skickas det till JSX blir det [object Object].
const readActionError = (error: unknown): string => {
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data as Record<string, unknown>;
    if (typeof data.details === 'string') return data.details;
    if (typeof data.error === 'string') return data.error;
    return error.message;
  }
  return error instanceof Error ? error.message : 'Okänt fel';
};

type ActionNotice = {
  succeed: (message: string) => void;
  fail: (message: string) => void;
};

const useFeedbackAction = <TResult, TVariables = void>({
  run,
  describe,
  invalidate,
  notice,
}: {
  run: (variables: TVariables) => Promise<TResult>;
  describe: (result: TResult) => string;
  invalidate: QueryKey[];
  notice: ActionNotice;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: run,
    onSuccess: (result) => {
      for (const queryKey of invalidate) {
        queryClient.invalidateQueries({ queryKey });
      }
      notice.succeed(describe(result));
    },
    onError: (error: unknown) => notice.fail(readActionError(error)),
  });
};

export const useFeedbackActions = (feedbackId: string) => {
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const notice: ActionNotice = {
    succeed: (message) => {
      setActionSuccess(message);
      setActionError(null);
    },
    fail: (message) => {
      setActionError(message);
      setActionSuccess(null);
    },
  };

  const detail: QueryKey = ['admin-feedback-detail', feedbackId];
  const queue: QueryKey = ['admin-feedback'];

  const statusMutation = useFeedbackAction({
    run: (status: FeedbackStatus) => updateFeedbackStatus(feedbackId, status),
    describe: () => 'Status uppdaterad',
    invalidate: [detail, queue],
    notice,
  });

  const triageMutation = useFeedbackAction({
    run: () => runTriage(feedbackId),
    describe: () => 'Triage klar',
    invalidate: [detail],
    notice,
  });

  const knowledgeMutation = useFeedbackAction({
    run: () => createKnowledgeArticle(feedbackId),
    describe: (result) => `Knowledge article skapad (ID: ${result.articleId})`,
    invalidate: [detail],
    notice,
  });

  const evalMutation = useFeedbackAction({
    run: () => createEvalCase(feedbackId),
    describe: (result) => `Eval case skapad (ID: ${result.evalCaseId})`,
    invalidate: [detail],
    notice,
  });

  const githubMutation = useFeedbackAction({
    run: () => createGithubIssue(feedbackId),
    describe: (result) =>
      result.existing
        ? `GitHub issue finns redan: ${result.issueUrl}`
        : `GitHub issue skapad: ${result.issueUrl}`,
    invalidate: [detail],
    notice,
  });

  return {
    actionError,
    actionSuccess,
    statusMutation,
    triageMutation,
    knowledgeMutation,
    evalMutation,
    githubMutation,
    isRunning:
      statusMutation.isPending ||
      triageMutation.isPending ||
      knowledgeMutation.isPending ||
      evalMutation.isPending ||
      githubMutation.isPending,
  };
};
